/**
 * Centralised API error message extractor.
 *
 * FastAPI returns HTTPException.detail as a STRING for explicit raise
 * (e.g. 400/404/409), but for Pydantic validation errors (422) it
 * returns an ARRAY of objects like:
 *
 *   [{ type, loc, msg, input, ctx, url }, ...]
 *
 * Passing such an object/array to `toast.error()` or rendering it
 * in JSX causes:
 *
 *   "Objects are not valid as a React child (found: object with
 *    keys {type, loc, ...})"
 *
 * which crashes the app via the ErrorBoundary.
 *
 * This helper always returns a SAFE human-readable string, no matter
 * what the API sent back.
 */
export function formatApiError(err, fallback = "Errore inatteso. Riprova.") {
  if (err == null) return fallback;

  // Plain string thrown by the app itself
  if (typeof err === "string") return err;

  // Axios error: drill into response.data
  const data = err?.response?.data;

  if (data != null) {
    // Backend explicit HTTPException(detail="…")
    if (typeof data === "string") return data;
    if (typeof data.detail === "string") return data.detail;

    // Pydantic validation errors (422) — array of { type, loc, msg, input }
    if (Array.isArray(data.detail)) {
      return data.detail
        .map((d) => {
          if (typeof d === "string") return d;
          const field = Array.isArray(d?.loc)
            ? d.loc.filter((p) => p !== "body").join(".")
            : "";
          const msg = d?.msg || "valore non valido";
          // Translate the most common Pydantic messages.
          const it =
            /value is not a valid enumeration member/i.test(msg) ||
            /Input should be/.test(msg)
              ? `Valore non ammesso per il campo '${field}'`
              : msg;
          return field ? `${field}: ${it}` : it;
        })
        .filter(Boolean)
        .join(" · ");
    }

    // Some endpoints return {message: "..."}
    if (typeof data.message === "string") return data.message;

    // Last resort: stringify the data object
    try {
      const s = JSON.stringify(data);
      if (s && s !== "{}" && s.length < 400) return s;
    } catch {
      // fall through
    }
  }

  // Network / generic JS Error
  if (typeof err?.message === "string") return err.message;
  return fallback;
}
